/** A component to display a progress based on discrete milestones */

import { twMerge } from 'tailwind-merge';
import cx from 'classnames';
import React from 'react';
import { Heading } from 'components/Typography';

export type MilestoneProgressProps = {
  /** List of discrete milestones values */
  milestones: string[];
  /** Current milestone reached */
  currentMilestoneValue: string;
  /** The title for the current milestone */
  currentMilestoneTitle: string;

  /** Target milestone to aim for */
  targetMilestoneValue: string;
  /** The title for the ideal milestone */
  targetMilestoneTitle: string;

  className?: string;
};

const MilestoneProgress: React.FC<MilestoneProgressProps> = ({
  className,
  currentMilestoneValue,
  targetMilestoneValue,
  currentMilestoneTitle,
  milestones,
  targetMilestoneTitle,
}) => {
  return (
    <div
      className={twMerge(
        cx('grid grid-flow-col auto-cols-fr', 'grid-rows-[repeat(3,minmax(0,auto))]', 'w-full gap-y-16'),
        className,
      )}
    >
      {milestones.map((milestone, index) => {
        const isCurrent = milestone === currentMilestoneValue;
        const isTargetMilestone = milestone === targetMilestoneValue;
        const isFirstCol = index === 0;
        const isLastCol = index === milestones.length - 1;
        return (
          <React.Fragment key={milestone}>
            <div className="flex flex-col justify-end grow text-center px-2">
              {isCurrent && (
                <Heading level="h8" className="font-semibold sm-d:text-lh9-sm">
                  {currentMilestoneTitle}
                </Heading>
              )}
              {isTargetMilestone && (
                <Heading level="h8" className="font-semibold text-black-400 sm-d:text-lh9-sm">
                  {targetMilestoneTitle}
                </Heading>
              )}
            </div>

            <div className="flex justify-center relative">
              <div className="absolute flex">
                {isCurrent && <div className="w-4 h-32 bg-black-100%" />}
                {isTargetMilestone && <div className="w-4 h-32 bg-purple-200" />}
              </div>
              <div
                className={cx('h-16 my-8 w-full bg-orchid-50', {
                  'rounded-l-full': isFirstCol,
                  'rounded-r-full': isLastCol,
                })}
              />
            </div>

            <Heading
              key={milestone}
              level="h9"
              className={cx('text-center', {
                'font-semibold': isCurrent,
              })}
            >
              {milestone}
            </Heading>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MilestoneProgress;
